import { CheckCircle2, FileText, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../components/custom/custom-button";
import CurrencyText from "../../../components/fragment/cur-text/CurrencyText";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import ReuseModal from "../../../components/fragment/modal/ReuseModal";
import { RazorInputField } from "../../../components/razor-reuse";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import {
  approveInfluencer,
  getInfluencerDetail,
  rejectInfluencer,
} from "../../../redux/influencer";
import RootState, {
  ApproveInfluencerPayload,
  ApproveInfluencerRewardConfig,
  InfluencerRewardType,
  ReferralQualifyingThreshold,
} from "../../../redux/type";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { formatDateToYYYYMMDD } from "../../../utils/Helper";
import NumberInput from "./components/NumberInput";
import QualifyingThresholdTable, {
  type QualifyingThresholdValue,
} from "./components/QualifyingThresholdTable";

export const INFLUENCER_APPLICATION_ID_KEY = "influencer_application_id";

const REWARD_TYPE_OPTIONS: { value: InfluencerRewardType; label: string }[] = [
  { value: "fixed", label: "Fixed Amount" },
  { value: "percent", label: "Percentage" },
];

const INFLUENCER_TYPE_LABELS: Record<string, string> = {
  macro_influencer: "Macro Influencer",
  micro_influencer: "Micro Influencer",
  ambassador: "Ambassador",
};

interface InfluencerRewardValue {
  referrerType: InfluencerRewardType;
  referrerValue: string;
  refereeType: InfluencerRewardType;
  refereeValue: string;
}

const DEFAULT_REWARD_VALUE: InfluencerRewardValue = {
  referrerType: "fixed",
  referrerValue: "",
  refereeType: "fixed",
  refereeValue: "",
};

/**
 * The backend sometimes serializes numeric fields as comma-formatted strings
 * (e.g. "1,500.00"). A native `<input type="number">` silently blanks any
 * value it can't parse, so strip formatting before it ever reaches state.
 */
function toPlainNumericString(value: number | string | null | undefined): string {
  if (value == null) return "";
  return String(value).replace(/,/g, "");
}

function buildQualifyingThresholds(
  values: Record<string, QualifyingThresholdValue>,
  currencies: any[],
): ReferralQualifyingThreshold[] {
  return (currencies || [])
    .filter((currency) => currency?.code)
    .map((currency) => {
      const value = values[currency.code] || { referral: "", referee: "" };
      return {
        currency_code: currency.code,
        qualifying_referrer_amount: Number(value.referral) || 0,
        qualifying_referee_amount: Number(value.referee) || 0,
      };
    });
}

function buildInfluencerRewards(
  values: Record<string, InfluencerRewardValue>,
  currencies: any[],
): ApproveInfluencerRewardConfig[] {
  return (currencies || [])
    .filter((currency) => currency?.code)
    .map((currency) => {
      const value = values[currency.code] || DEFAULT_REWARD_VALUE;
      return {
        currency_code: currency.code,
        referrer_reward_type: value.referrerType,
        referrer_reward_value: Number(value.referrerValue) || 0,
        referee_reward_type:
          value.refereeValue === "" ? null : value.refereeType,
        referee_reward_value:
          value.refereeValue === "" ? null : Number(value.refereeValue),
      };
    });
}

export default function InfluencerApplication() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    influencer_detail: application,
    loading_influencer_detail,
    loading_influencer_approve,
    loading_influencer_reject,
  } = useSelector((state: RootState) => state.influencer);
  const { all_currencies_drop } = useSelector(
    (state: RootState) => state.settings,
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const canApprove = !!user?.permissions?.includes(
    "approve_influencer_contract",
  );
  const canReject = !!user?.permissions?.includes(
    "reject_influencer_contract",
  );

  const [applicationId] = useState(() =>
    localStorage.getItem(INFLUENCER_APPLICATION_ID_KEY),
  );

  const goToInfluencerTab = () => {
    navigate(ALL_ROUTES?.DASHBOARD_REFERRAL, {
      state: { activeTab: "Influencer Programme" },
    });
  };

  // redirect if user has no permission
  useEffect(() => {
    if (
      !user?.permissions?.includes("view_influencer_contract") &&
      user?.permissions?.length > 0
    ) {
      navigate(ALL_ROUTES.DASHBOARD_REFERRAL);
    }
  }, [user?.permissions]);

  useEffect(() => {
    if (!applicationId) {
      goToInfluencerTab();
      return;
    }
    dispatch(getInfluencerDetail(applicationId) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contract = application?.influencer_contract || null;
  const hasContract = !!contract;
  // A contract can outlive a later rejection (e.g. re-reviewed and rejected
  // after a prior approval) — "approved" only counts while the application
  // isn't in a rejected state.
  const isApproved = hasContract && application?.status !== "rejected";
  const isRejected = application?.status === "rejected";
  const isReadOnly = isApproved || isRejected;

  const [targetUsers, setTargetUsers] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [qualifyingThresholds, setQualifyingThresholds] = useState<
    Record<string, QualifyingThresholdValue>
  >({});
  const [rewards, setRewards] = useState<Record<string, InfluencerRewardValue>>(
    {},
  );
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    if (!application) return;
    if (contract) {
      setTargetUsers(toPlainNumericString(contract.target));
      setReferralCode(contract.referral_code || "");
      setStartDate(contract.starts_at ? contract.starts_at.split("T")[0] : "");
      setEndDate(contract.ends_at ? contract.ends_at.split("T")[0] : "");
      setQualifyingThresholds(
        Object.fromEntries(
          (contract.qualifying_thresholds || [])
            .filter((threshold) => threshold?.currency_code)
            .map((threshold) => [
              threshold.currency_code,
              {
                referral: toPlainNumericString(
                  threshold?.qualifying_referrer_amount,
                ),
                referee: toPlainNumericString(
                  threshold?.qualifying_referee_amount,
                ),
              },
            ]),
        ),
      );
      setRewards(
        Object.fromEntries(
          (contract.rewards || [])
            .filter((reward) => reward?.currency_code)
            .map((reward) => [
              reward.currency_code,
              {
                referrerType: reward?.referrer_reward_type || "fixed",
                referrerValue: toPlainNumericString(
                  reward?.referrer_reward_value,
                ),
                refereeType: reward?.referee_reward_type || "fixed",
                refereeValue: toPlainNumericString(reward?.referee_reward_value),
              },
            ]),
        ),
      );
    } else {
      setTargetUsers(toPlainNumericString(application.target));
      setReferralCode(application.preferred_referral_code || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  const updateQualifyingThreshold = (
    code: string,
    field: keyof QualifyingThresholdValue,
    nextValue: string,
  ) => {
    setQualifyingThresholds((prev) => {
      const current = prev[code] || { referral: "", referee: "" };
      return { ...prev, [code]: { ...current, [field]: nextValue } };
    });
  };

  const updateReward = (
    code: string,
    field: keyof InfluencerRewardValue,
    nextValue: string,
  ) => {
    setRewards((prev) => {
      const current = prev[code] || DEFAULT_REWARD_VALUE;
      return { ...prev, [code]: { ...current, [field]: nextValue } };
    });
  };

  const handleApprove = async () => {
    if (!application) return;

    const currencies = all_currencies_drop || [];
    const payload: ApproveInfluencerPayload = {
      referral_code: referralCode,
      target: targetUsers === "" ? null : Number(targetUsers),
      starts_at: startDate ? `${startDate}T00:00:00.000Z` : "",
      ends_at: endDate ? `${endDate}T00:00:00.000Z` : null,
      qualifying_thresholds: buildQualifyingThresholds(
        qualifyingThresholds,
        currencies,
      ),
      rewards: buildInfluencerRewards(rewards, currencies),
    };

    const result: any = await dispatch(
      approveInfluencer({ id: application.id, payload }) as any,
    );
    if (result?.payload?.data?.status) {
      goToInfluencerTab();
    }
  };

  const handleConfirmReject = async () => {
    if (!application || !rejectionReason.trim()) return;

    const result: any = await dispatch(
      rejectInfluencer({
        id: application.id,
        payload: { rejection_reason: rejectionReason.trim() },
      }) as any,
    );
    if (result?.payload?.data?.status) {
      setRejectModalOpen(false);
      goToInfluencerTab();
    }
  };

  const tabLinkList = [
    { label: "Overview", active: false },
    { label: "Referral Settings", active: false },
    { label: "Promotions", active: false },
    { label: "Influencer Programme", active: true },
  ];

  const isInitialLoad = loading_influencer_detail && !application;

  if (isInitialLoad) {
    return (
      <DashboardLayoutMain
        title="Referral Program"
        back
        onBack={goToInfluencerTab}
      >
        <div className="mx-[2rem] py-[40px] space-y-[16px]">
          <Skeleton height={120} />
          <Skeleton height={200} />
          <Skeleton height={280} />
        </div>
      </DashboardLayoutMain>
    );
  }

  if (!application) return null;

  return (
    <DashboardLayoutMain
      title="Referral Program"
      back
      onBack={goToInfluencerTab}
    >
      <section className="bg-white mx-[2rem] pt-[10px] space-y-4 mt-[15px]">
        <TabLink
          onClick={(param) => {
            if (param?.label === "Influencer Programme") {
              goToInfluencerTab();
            } else {
              navigate(ALL_ROUTES?.DASHBOARD_REFERRAL, {
                state: { activeTab: param?.label },
              });
            }
          }}
          list={tabLinkList}
          className="single-customer-tab-link"
        />
      </section>

      <div className="mx-[2rem] py-[16px] space-y-[20px] max-w-[900px]">
        {/* uploaded documents */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Uploaded Documents
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            View of uploaded documents from the influencer side
          </p>

          <div className="mt-[16px]">
            {application.document_file_path ? (
              <a
                href={application.document_file_path}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-[220px] flex-col items-center gap-[8px] rounded-[8px] border border-[#E4E4E7] p-[20px] text-center hover:border-[#734792]"
              >
                <span className="flex size-[36px] items-center justify-center rounded-full bg-[#F4F4F5] text-[#A1A1AA]">
                  <FileText className="size-[16px]" />
                </span>
                <span className="text-[14px] font-bold text-[#1E1E1E]">
                  Influencer doc
                </span>
                <span className="text-[13px] text-[#758089]">
                  Document submission
                </span>
              </a>
            ) : (
              <p className="text-[13px] text-[#A1A1AA]">No document uploaded</p>
            )}
          </div>
        </section>

        {/* personal details */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[16px] font-bold text-[#1E1E1E]">
                Personal Details
              </p>
              <p className="text-[13px] text-[#758089] mt-[2px]">
                User details
              </p>
            </div>
            <div className="flex items-center gap-[8px]">
              <span className="text-[13px] text-[#758089]">Category:</span>
              <span className="inline-flex items-center gap-[6px] rounded-full bg-[#F4EDFB] px-[12px] py-[6px] text-[13px] font-medium text-[#734792]">
                {INFLUENCER_TYPE_LABELS[application.influencer_type] ||
                  application.influencer_type}
              </span>
            </div>
          </div>

          <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            <div>
              <label className="text-[14px] text-[#758089]">Name</label>
              <input
                disabled
                readOnly
                value={application.full_name}
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-[#F9FAFB] px-[12px] text-[14px] text-[#1E1E1E] outline-none"
              />
            </div>
            <div>
              <label className="text-[14px] text-[#758089]">Email</label>
              <input
                disabled
                readOnly
                value={application.email}
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-[#F9FAFB] px-[12px] text-[14px] text-[#1E1E1E] outline-none"
              />
            </div>
            <div>
              <label className="text-[14px] text-[#758089]">Phone</label>
              <input
                disabled
                readOnly
                value={application.phone_number}
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-[#F9FAFB] px-[12px] text-[14px] text-[#1E1E1E] outline-none"
              />
            </div>
            <div>
              <label className="text-[14px] text-[#758089]">
                Country of Residence
              </label>
              <input
                disabled
                readOnly
                value={application.country?.name || "—"}
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-[#F9FAFB] px-[12px] text-[14px] text-[#1E1E1E] outline-none"
              />
            </div>
          </div>
        </section>

        {/* influencer settings */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Influencer settings
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Minimum threshold to qualify for referral bonus
          </p>

          {isApproved && (
            <div className="mt-[12px] flex items-start gap-[10px] rounded-[8px] bg-[#DCFCE7] px-[16px] py-[12px]">
              <CheckCircle2 className="size-[16px] shrink-0 mt-[2px] text-[#16A34A]" />
              <div>
                <p className="text-[13px] font-medium text-[#15803D]">
                  Application approved
                </p>
                <p className="text-[13px] text-[#166534] mt-[2px]">
                  Settings below reflect the active contract and can no
                  longer be edited here.
                </p>
              </div>
            </div>
          )}

          {isRejected && application.rejection_reason && (
            <div className="mt-[12px] flex items-start gap-[10px] rounded-[8px] bg-[#FDECEC] px-[16px] py-[12px]">
              <XCircle className="size-[16px] shrink-0 mt-[2px] text-[#DC2626]" />
              <div>
                <p className="text-[13px] font-medium text-[#DC2626]">
                  Application rejected
                </p>
                <p className="text-[13px] text-[#3F3F46] mt-[2px]">
                  {application.rejection_reason}
                </p>
              </div>
            </div>
          )}

          <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            <div>
              <label className="text-[14px] text-[#758089]">Target Users</label>
              <NumberInput
                min={0}
                disabled={isReadOnly}
                value={targetUsers}
                onChange={(e) => setTargetUsers(e.target.value)}
                placeholder="No target"
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] disabled:bg-[#F9FAFB]"
              />
            </div>
            <div>
              <label className="text-[14px] text-[#758089]">
                Referral Code
              </label>
              <input
                disabled={isReadOnly}
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                maxLength={255}
                placeholder="Enter referral code"
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] disabled:bg-[#F9FAFB]"
              />
            </div>
          </div>

          <div className="mt-[16px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            <RazorInputField
              color="black-light"
              type="date"
              label="Start Date"
              placeholder=" Select date"
              disabled={isReadOnly}
              value={startDate}
              onChange={(date: any) =>
                setStartDate(formatDateToYYYYMMDD(date[0]))
              }
            />
            <RazorInputField
              color="black-light"
              type="date"
              label="End Date"
              placeholder=" Select date"
              disabled={isReadOnly}
              value={endDate}
              onChange={(date: any) =>
                setEndDate(formatDateToYYYYMMDD(date[0]))
              }
              dateOptions={{ minDate: startDate || undefined }}
            />
          </div>

          <p className="mt-[12px] rounded-[8px] bg-[#EFF6FF] px-[14px] py-[10px] text-[13px] text-[#1D4ED8]">
            The code becomes active on the start date and auto-expires at the
            end date. Any existing default referral code will be deactivated
          </p>
        </section>

        {/* qualifying threshold */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Qualifying Threshold
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            The minimum amount a referred user must complete to qualify the
            referral, per currency
          </p>

          <QualifyingThresholdTable
            currencies={all_currencies_drop || []}
            values={qualifyingThresholds}
            onChange={updateQualifyingThreshold}
            disabled={isReadOnly}
          />
        </section>

        {/* reward settings */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Reward Settings
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Configure the referrer and referee reward for this influencer, per
            currency
          </p>

          <div className="mt-[16px] space-y-[16px]">
            {(all_currencies_drop || [])
              .filter((currency: any) => currency?.code)
              .map((currency: any) => {
              const value = rewards[currency.code] || DEFAULT_REWARD_VALUE;
              return (
                <div
                  key={currency.code}
                  className="rounded-[8px] bg-[#F4F4F5] p-[16px]"
                >
                  <CurrencyText
                    currency={currency.code}
                    text={currency?.name}
                    imgSize="20px"
                  />

                  <div className="mt-[12px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
                    <div>
                      <label className="text-[13px] text-[#758089]">
                        Referrer Reward Type
                      </label>
                      <select
                        disabled={isReadOnly}
                        value={value.referrerType}
                        onChange={(e) =>
                          updateReward(
                            currency.code,
                            "referrerType",
                            e.target.value,
                          )
                        }
                        className="mt-[6px] h-[40px] w-full appearance-none rounded-[8px] border border-[#E4E4E7] bg-white px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] disabled:bg-[#F9FAFB]"
                      >
                        {REWARD_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[13px] text-[#758089]">
                        Referrer Reward Amount
                        <span className="text-[#DC2626]">*</span>
                      </label>
                      <NumberInput
                        thousandSeparator
                        disabled={isReadOnly}
                        value={value.referrerValue}
                        onChange={(e) =>
                          updateReward(
                            currency.code,
                            "referrerValue",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. 250"
                        className="mt-[6px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-white px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] disabled:bg-[#F9FAFB]"
                      />
                    </div>
                    <div>
                      <label className="text-[13px] text-[#758089]">
                        Referee Reward Type
                      </label>
                      <select
                        disabled={isReadOnly}
                        value={value.refereeType}
                        onChange={(e) =>
                          updateReward(
                            currency.code,
                            "refereeType",
                            e.target.value,
                          )
                        }
                        className="mt-[6px] h-[40px] w-full appearance-none rounded-[8px] border border-[#E4E4E7] bg-white px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] disabled:bg-[#F9FAFB]"
                      >
                        {REWARD_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[13px] text-[#758089]">
                        Referee Reward Amount (optional)
                      </label>
                      <NumberInput
                        thousandSeparator
                        disabled={isReadOnly}
                        value={value.refereeValue}
                        onChange={(e) =>
                          updateReward(
                            currency.code,
                            "refereeValue",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. 500"
                        className="mt-[6px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-white px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] disabled:bg-[#F9FAFB]"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* actions */}
        <section className="flex items-center gap-[12px] pb-[40px]">
          <Button
            variant="ghost"
            onClick={goToInfluencerTab}
            className="bg-[#FDECEC] text-[#DC2626] hover:bg-[#FDECEC] hover:text-[#DC2626]"
          >
            Cancel
          </Button>
          {!isReadOnly && canReject && (
            <Button
              variant="destructive-outline"
              onClick={() => setRejectModalOpen(true)}
            >
              Reject
            </Button>
          )}
          {!isRejected && canApprove && (
            <Button
              disabled={isReadOnly}
              isLoading={loading_influencer_approve}
              onClick={handleApprove}
            >
              {isApproved ? "Already Approved" : "Create Influencer"}
            </Button>
          )}
        </section>
      </div>

      <ReuseModal
        visible={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onBtnClick={handleConfirmReject}
        loading={loading_influencer_reject}
        disable={!rejectionReason.trim()}
        deleteType
        btnLabel="Reject Application"
        title="Reject Influencer Application"
        deleteText={`Are you sure you want to reject ${application.full_name}'s application?`}
      >
        <textarea
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          rows={3}
          placeholder="Reason for rejection"
          className="mt-[12px] w-full resize-none rounded-[8px] border border-[#E4E4E7] px-[12px] py-[10px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
        />
      </ReuseModal>
    </DashboardLayoutMain>
  );
}
